import { useDeferredValue, useId, useMemo, useState } from 'react';
import { lifeYears } from '../domain/dates';
import { displayName, searchTerms, similarity } from '../domain/names';
import type { PersonWithNames, UUID } from '../domain/types';
import { useI18n } from '../i18n';
import { Avatar, PersonName } from './Avatar';
import { Icon } from './Icon';

export function rankPeople(people: PersonWithNames[], query: string, limit = 8): PersonWithNames[] {
  const q = query.trim().toLowerCase();
  if (!q) return people.slice(0, limit);
  const scored = people
    .map((p) => {
      let best = 0;
      for (const term of searchTerms(p)) {
        const s = term.includes(q) ? 1 : term.startsWith(q) ? 0.95 : similarity(term, q);
        if (s > best) best = s;
      }
      return { p, best };
    })
    .filter((x) => x.best >= 0.45)
    .sort((a, b) => b.best - a.best);
  return scored.slice(0, limit).map((x) => x.p);
}

/** Search-and-select one person. Used by add-relative, sentence builder and filters. */
export function PersonPicker({
  people,
  value,
  onChange,
  label,
  placeholder,
  exclude = [],
  allowClear,
  autoFocus,
  quiet,
}: {
  people: PersonWithNames[];
  value: UUID | null;
  onChange: (id: UUID | null) => void;
  label?: string;
  placeholder?: string;
  exclude?: UUID[];
  allowClear?: boolean;
  autoFocus?: boolean;
  /**
   * Offer nobody until something has been typed.
   *
   * By default the first few people are listed straight away, which is right
   * where choosing a person is the whole job (adding a relative). Where it is one
   * field among several — a filter above a list, a form in Admin controls — eight
   * rows of the family's oldest ancestors are a screenful of phone to scroll past
   * on the way to the thing the screen is actually for.
   */
  quiet?: boolean;
}) {
  const { t, lang } = useI18n();
  const id = useId();
  const [query, setQuery] = useState('');
  /*
   * The box shows what was typed at once; the list catches up a moment behind
   * it. Ranking a large family is real work, and doing it *inside* the keystroke
   * is what makes typing a name feel sticky on a phone — deferred, React fits it
   * in between keystrokes and drops a ranking nobody will see.
   */
  const asked = useDeferredValue(query);
  const selected = people.find((p) => p.id === value);
  const candidates = useMemo(() => rankPeople(people.filter((p) => !exclude.includes(p.id)), asked), [people, asked, exclude.join(',')]);

  if (selected) {
    return (
      <div className="field">
        {label ? <span className="field-label">{label}</span> : null}
        <div className="row-nowrap card-soft" style={{ padding: 'var(--s-2) var(--s-3)' }}>
          <Avatar person={selected} lang={lang} size="sm" />
          <span className="grow bold">
            <PersonName person={selected} lang={lang} />
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              onChange(null);
              setQuery('');
            }}
          >
            {allowClear ? t('common.clear') : t('common.search')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="field">
      {label ? (
        <label className="field-label" htmlFor={id}>
          {label}
        </label>
      ) : null}
      <input
        id={id}
        className="input"
        value={query}
        autoFocus={autoFocus}
        placeholder={placeholder ?? t('picker.placeholder')}
        onChange={(e) => setQuery(e.target.value)}
        role="combobox"
        aria-expanded={candidates.length > 0 && !(quiet && !query.trim())}
        aria-controls={`${id}-list`}
        autoComplete="off"
      />
      <ul className="list-reset" id={`${id}-list`} style={{ maxHeight: 260, overflowY: 'auto' }}>
        {(quiet && !query.trim() ? [] : candidates).map((p) => (
          <li key={p.id}>
            <button type="button" className="person-row" onClick={() => onChange(p.id)}>
              <Avatar person={p} lang={lang} size="sm" />
              <span className="grow">
                <span className="name">
                  <PersonName person={p} lang={lang} />
                </span>
                <span className="sub"> {lifeYears(p.birth, p.death, p.status === 'deceased', lang)}</span>
              </span>
              <Icon name="forward" className="icon-mirror faint" />
            </button>
          </li>
        ))}
        {query.trim() && candidates.length === 0 ? <li className="muted small" style={{ padding: 'var(--s-2)' }}>{t('find.noResults', { q: query })}</li> : null}
      </ul>
    </div>
  );
}

/** Multi-select chips for "people in this story". */
export function PeopleMultiPicker({
  people,
  value,
  onChange,
  label,
  help,
}: {
  people: PersonWithNames[];
  value: UUID[];
  onChange: (ids: UUID[]) => void;
  label?: string;
  help?: string;
}) {
  const { t, lang } = useI18n();
  const id = useId();
  const [query, setQuery] = useState('');
  const asked = useDeferredValue(query);
  const selected = value.map((v) => people.find((p) => p.id === v)).filter(Boolean) as PersonWithNames[];
  const candidates = useMemo(() => rankPeople(people.filter((p) => !value.includes(p.id)), asked, 6), [people, asked, value.join(',')]);

  return (
    <div className="field">
      {label ? (
        <label className="field-label" htmlFor={id}>
          {label}
        </label>
      ) : null}
      {help ? <span className="help">{help}</span> : null}
      {selected.length > 0 && (
        <div className="chip-row">
          {selected.map((p) => (
            <button key={p.id} type="button" className="chip chip-btn selected" onClick={() => onChange(value.filter((v) => v !== p.id))}>
              <PersonName person={p} lang={lang} />
              <Icon name="close" />
              <span className="sr-only">{t('common.remove')}</span>
            </button>
          ))}
        </div>
      )}
      <input id={id} className="input" value={query} placeholder={t('picker.placeholder')} onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
      {query.trim() && (
        <div className="chip-row">
          {candidates.map((p) => (
            <button
              key={p.id}
              type="button"
              className="chip chip-btn"
              onClick={() => {
                onChange([...value, p.id]);
                setQuery('');
              }}
            >
              <Icon name="plus" />
              <PersonName person={p} lang={lang} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
